import React, { useCallback } from "react";
import RipplePulse from "components/common/loaders/RipplePulse";
import GenericButton from "components/common/buttons/GenericButton";
import { useLocale } from "lib/hooks/useLocale";
import NotPlaced from "components/payments/dialogs/NotPlaced";
import { usePaymentContext } from "lib/contexts/PaymentProvider";
import { GenericDialog } from "components/common/dialogs/GenericDialog";
import { Warning } from "@phosphor-icons/react";
import { Icon } from "@shopflo/ui/icon";

const PaymentProcessing: React.FC<{}> = ({}) => {
  const { t } = useLocale();
  const {
    state: { isProcessingDialogVisible, paymentStatus, isCancellable },
    actions: { hidePaymentProcessingDialog, handleCancelPayment },
  } = usePaymentContext();

  const processingMsg = useCallback(() => {
    if (paymentStatus === "PROCESSING" && !isCancellable) return t("placing_order");
    if (paymentStatus === "PROCESSING") return t("payment_processing");
    if (paymentStatus === "PLACING_ORDER") return t("placing_order");
    if (paymentStatus === "PROCESSING_UPI") return t("payment_processing_upi");
    return t("payment_processing");
  }, [paymentStatus]);

  const renderCancellableContent = (
    <>
      <div
        className={`flex flex-col items-center justify-center px-4 pt-6 pb-12 ${
          paymentStatus === "ORDER_FAILED" && "h-full"
        }`}>
        {paymentStatus === "ORDER_FAILED" ? (
          <NotPlaced />
        ) : (
          <div className="flex flex-col items-center max-w-md w-full">
            {/* Title Section */}
            <div className="flex flex-col items-center space-y-3 mb-6">
              <h2 className="text-xl font-semibold text-center text-coal-dark">{processingMsg()}</h2>
              <p className="text-center text-sm text-coal-light px-4 leading-relaxed">
                {t("payment_do_not_cancel")}
              </p>
            </div>

            {/* Warning Message */}
            <div className="mb-8 px-4">
              <p className="text-center text-xs font-medium text-red-600 flex items-center justify-center gap-1.5">
                <Icon IconComponent={Warning} size="xs" />
                <span className="text-text-xs-semibold mt-1">Please don't refresh or click back</span>
              </p>
            </div>

            {/* Loading Animation */}
            <div className="flex flex-col items-center space-y-4 mb-10">
              <RipplePulse />
            </div>

            {/* Cancel Link */}
            {isCancellable && handleCancelPayment && (
              <p className="text-sm text-coal-light text-center">
                Could not complete payment?{" "}
                <span
                  className="text-coal-dark font-medium cursor-pointer underline hover:text-coal-dark/80 transition-colors"
                  onClick={() => {
                    handleCancelPayment();
                  }}>
                  Cancel
                </span>
              </p>
            )}
          </div>
        )}
      </div>
    </>
  );

  return (
    <GenericDialog
      isOpen={isProcessingDialogVisible}
      setIsOpen={(value: boolean) => hidePaymentProcessingDialog()}
      translateAxis="y"
      dialogOverlay={true}
      customClass={`bottom-0 overflow-scroll ${
        paymentStatus === "ORDER_FAILED" ? "h-screen md:h-[85vh]" : "!top-auto md:absolute !rounded-t-2xl"
      }`}
      modalType="PROCESSING">
      <>
        {isCancellable ? (
          renderCancellableContent
        ) : (
          <div
            className={`flex flex-col items-center justify-center space-y-6 px-4 pt-6 pb-12 ${
              paymentStatus === "ORDER_FAILED" && "h-full"
            }`}>
            {paymentStatus === "ORDER_FAILED" ? (
              <NotPlaced />
            ) : (
              <>
                <p className="text-center font-light">{processingMsg()}</p>
                <div className="mb-6">
                  <RipplePulse />
                </div>
              </>
            )}
          </div>
        )}
      </>
    </GenericDialog>
  );
};

export default React.memo(PaymentProcessing);
